import { useEffect } from 'react';
import type { DefinedInitialDataOptions } from '@tanstack/react-query';
import type { WhoAmIContext } from '@interxfi/api-client/realm-auth/store';
import type { IxmUserauthV1SessionServiceWhoamiResponse } from '@interxfi/types';
import {
  useRealmAuthIsAuthenticated,
  useRealmAuthReset,
  useRealmAuthSetContext,
  useRealmAuthSetIsAuthenticated,
  useRealmAuthSetIsAuthenticating,
} from '../context';
import { useIxmUserauthV1SessionServiceWhoami } from '../generated/hooks/ixm-userauth-v1-session-service-whoami';

const WHOAMI_STALE_TIME = 1_000 * 60 * 60; // 1 hour

export function useWhoami(
  onUnauthenticated?: VoidFunction,
  options?: Partial<DefinedInitialDataOptions<IxmUserauthV1SessionServiceWhoamiResponse | undefined>>,
) {
  const isAuthenticated = useRealmAuthIsAuthenticated();
  const setIsAuthenticated = useRealmAuthSetIsAuthenticated();
  const setIsAuthenticating = useRealmAuthSetIsAuthenticating();
  const setContext = useRealmAuthSetContext();
  const reset = useRealmAuthReset();

  const query = useIxmUserauthV1SessionServiceWhoami({
    refetchOnWindowFocus: isAuthenticated,
    staleTime: WHOAMI_STALE_TIME,
    retry: false,
    ...options,
  });

  useEffect(() => {
    if (isAuthenticated && !query.isPending && !query.isStale && (query.error || !query.data?.user)) {
      reset();
      onUnauthenticated?.();
    }
  }, [isAuthenticated, query.error, query.isPending, query.isStale, query.data?.user, reset, onUnauthenticated]);

  useEffect(() => {
    if (query.data) {
      setIsAuthenticated(Boolean(query.data.user));

      if (query.data.user) {
        const ctx: WhoAmIContext = {
          ...query.data.user,
          hasMFA: Boolean(query.data.hasMfa),
          organizationMemberships: query.data.organizations || [],
          accesses: query.data.realms || [],
          sessionExpiresAt: query.data.sessionExpiry ? new Date(query.data.sessionExpiry) : undefined,
          sessionId: query.data.sessionId,
        };

        setContext(() => ctx);
        setIsAuthenticating(false);
      }
    }
  }, [query.data, setContext, setIsAuthenticated, setIsAuthenticating]);

  useEffect(() => {
    setIsAuthenticating(query.isPending);
  }, [query.isPending, setIsAuthenticating]);

  return query;
}
